import * as React from 'react'
import classNames from 'classnames'
import { CSSResetFrontinoProvider } from '@conekta/frontino-ui/v2'
import { FOCUSABLE_ELEMENT_SELECTORS } from './constants'
import {
  IBody,
  IChildren,
  ICloseButton,
  IContentWindow,
  IFocusNextElement,
  IModal,
  ITitle,
  ITrapFocus,
  IWrapper
} from './interface'
import { UIContext } from 'shared/context/UIContext'
import './Modal.scss'

const Modal = ({ children, data }: IModal): JSX.Element => {
  const { useContext, useEffect } = React
  const { removeModal } = useContext(UIContext)
  const { actions, canModalBeRemoved = true, closeOnclickOutside = false, width } = data
  const css: Record<string, string> = width ? { maxWidth: width } : {}

  useEffect(() => {
    document.addEventListener('keydown', e => getKeyAndRemoveModal(e))
    return () => document.removeEventListener('keydown', getKeyAndRemoveModal)
  })

  const clickOutsideCloseModal = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLDivElement

    if (e && target?.className?.includes) {
      if (target.className.includes('Modal__wrapper')) {
        closeOnclickOutside && removeModal()
      }
    }
  }

  const getKeyAndRemoveModal = (e: KeyboardEvent): void => {
    if (e.keyCode === 27) {
      canModalBeRemoved && removeModal()
    }
  }

  return (
    <Wrapper className={data.className || ''} onClick={clickOutsideCloseModal}>
      <ContentWindow css={css}>
        {canModalBeRemoved && <CloseButton onClick={removeModal} />}
        <Header>
          <Title text={data.title || ''} />
        </Header>
        <Body actions={actions}>{children}</Body>
      </ContentWindow>
    </Wrapper>
  )
}

const Wrapper = ({ children, className, onClick }: IWrapper): JSX.Element => (
  <div className={classNames('Modal__wrapper', className)} onClick={(e: React.MouseEvent<HTMLElement>) => onClick(e)}>
    <CSSResetFrontinoProvider scope='Modal__wrapper'>{children}</CSSResetFrontinoProvider>
  </div>
)

const ContentWindow = ({ children, css }: IContentWindow): JSX.Element => (
  <div className='Modal__contentWindow' style={css}>
    {children}
  </div>
)

const Header = ({ children }: IChildren): JSX.Element => <div className='Modal__header'>{children}</div>

const Title = ({ text }: ITitle) => (text ? <p className='Modal__title'>{text}</p> : null)

const CloseButton = ({ onClick }: ICloseButton): JSX.Element => (
  <div className='Modal__btn--close' onClick={(e: React.MouseEvent<HTMLElement>): void => onClick(e)}>
    <i className='icon-close'></i>
  </div>
)

const Body = ({ actions, children }: IBody): JSX.Element => {
  const { useEffect, useRef } = React
  const MODAL_BODY_REF: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

  useEffect(() => {
    document.addEventListener('keydown', e => trapFocusOnModal(e))
    focusFirstFocusableElement()
    return () => document.removeEventListener('keydown', trapFocusOnModal)
  }, [])

  const focusFirstFocusableElement = (): void => {
    const NODE = MODAL_BODY_REF.current as any

    if (NODE) {
      getFocusableElements(NODE)[0]?.focus()
    }
  }

  const focusNextElement = ({ currentElement, e, nextElement }: IFocusNextElement): void => {
    if (document.activeElement === currentElement) {
      e.preventDefault()
      nextElement?.focus()
    }
  }

  const getFocusableElements = (node: any): NodeListOf<HTMLElement> =>
    node?.querySelectorAll(FOCUSABLE_ELEMENT_SELECTORS)

  const isTabKeyPressed = (e: KeyboardEvent): boolean => e.key === 'Tab' || e.keyCode === 9

  const trapFocus = ({ e, elementList }: ITrapFocus): void => {
    const FIRST_FOCUSABLE_ELEMENT = elementList[0]
    const LAST_FOCUSABLE_ELEMENT = elementList[elementList.length - 1]

    if (e.shiftKey) {
      focusNextElement({
        currentElement: FIRST_FOCUSABLE_ELEMENT,
        e,
        nextElement: LAST_FOCUSABLE_ELEMENT
      })
    } else {
      focusNextElement({
        currentElement: LAST_FOCUSABLE_ELEMENT,
        e,
        nextElement: FIRST_FOCUSABLE_ELEMENT
      })
    }
  }

  const trapFocusOnModal = (e: KeyboardEvent): void => {
    if (isTabKeyPressed(e)) {
      const NODE = MODAL_BODY_REF.current

      if (NODE) {
        const FOCUSABLE_CONTENT = getFocusableElements(NODE)
        FOCUSABLE_CONTENT.length > 0 ? trapFocus({ e, elementList: FOCUSABLE_CONTENT }) : e.preventDefault()
      }
    }
  }

  return (
    <div className='Modal__body' ref={MODAL_BODY_REF}>
      <div className='Modal__children'>{children}</div>
      {actions ? <div className='modal-actions'>{actions}</div> : <></>}
    </div>
  )
}

export default Modal
